import {
  defaultFiscalProviderFor,
  marketPaymentMode,
  marketPspChoices,
  PaymentMode,
  PaymentProvider,
  type FiscalProvider,
  type MarketConfig,
} from '@etiquetachef/types';

/*
 * Valor dos campos de PSP de um mercado (`MarketPspFields`) e a conversão para
 * o input do `setMarketConfig`. Puro, fora do componente para o Fast Refresh.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7).
 * Usado por: `MarketPspFields.tsx`, `MarketConfigEditDialog.tsx`,
 * `features/countries/CountryEditDialog.tsx`.
 * Histórico: 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão nos exports (#796).
 * 2026-10-13 11:23 +0200 — (#958) sem entidade vendedora: o país escolhe só o
 * PSP, e a PJ sai do PSP (`findSellerEntityForProvider`); `seller_entity_id`
 * saiu do valor e do payload.
 */

/** O que os campos de PSP guardam: a opção `provider:mode` e os extras. */
export type MarketPspValue = {
  readonly choice: string;
  readonly extraAllowed: readonly string[];
  /**
   * Como o doc estava ao abrir: a opção (`provider:mode`, mesmo fora do
   * seletor) e o fiscal gravado. `null` em país sem doc. É o que permite salvar
   * outro campo do país sem trocar o PSP dele (auditoria #756, achado 1).
   */
  readonly initial: { readonly choice: string; readonly fiscal: FiscalProvider } | null;
};

/** O pedaço do `setMarketConfig` que estes campos produzem (mercado vendável). */
export type MarketPspPayload = {
  payment_provider: string;
  payment_mode: PaymentMode;
  allowed_payment_providers: string[];
  fiscal_provider: FiscalProvider;
};

/** Separa a opção `provider:mode` do seletor. */
export function splitPspChoice(choice: string): { provider: string; mode: PaymentMode } {
  const [provider = PaymentProvider.STRIPE, mode] = choice.split(':');
  return { provider, mode: mode === PaymentMode.SANDBOX ? PaymentMode.SANDBOX : PaymentMode.LIVE };
}

/**
 * O valor inicial a partir do doc do mercado. Doc sem `payment_mode` é live;
 * país sem doc ou sem PSP abre no Stripe live.
 *
 * Auditoria 2026-10-01 (#756, achado 1, alto): PSP fora do seletor (o Paddle da
 * IT, vendável em produção) abria como Stripe live, e salvar QUALQUER campo do
 * país (fuso, OTP) gravava `payment_provider: stripe` sem ninguém pedir — a IT
 * passaria a cobrar cliente real pelo Stripe sem SDI. Agora a opção atual é
 * mantida (o seletor a mostra como "atual") e o fiscal gravado é preservado
 * enquanto o PSP não muda. Não voltar ao fallback para Stripe aqui.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7).
 *
 * Dados: `current` é o doc `/config/markets/items/{country}` (`MarketConfig`)
 * ou `null`; doc sem `payment_mode` (legado) conta como live.
 *
 * Usado por: `MarketConfigEditDialog` (`/billing/markets`) e
 * `features/countries/CountryEditDialog.tsx` (`/countries`), admin web.
 */
export function marketPspInitial(country: string, current: MarketConfig | null): MarketPspValue {
  const provider = current?.payment_provider ?? PaymentProvider.STRIPE;
  const mode = current ? marketPaymentMode(current) : PaymentMode.LIVE;
  const choice = `${provider}:${mode}`;
  const selectable = new Set<string>(marketPspChoices(country).map((c) => c.provider));
  return {
    choice,
    // Só os extras que o seletor mostra: um extra invisível iria no payload sem
    // o admin poder vê-lo nem tirá-lo (auditoria #756, baixo).
    extraAllowed: (current?.allowed_payment_providers ?? []).filter((p) => p !== provider && selectable.has(p)),
    initial: current?.payment_provider ? { choice, fiscal: current.fiscal_provider } : null,
  };
}

/** A opção ainda é a que o doc tinha ao abrir (inclusive um PSP fora do seletor)? */
export function isInitialChoice(v: MarketPspValue): boolean {
  return v.initial !== null && v.initial.choice === v.choice;
}

/**
 * Converte o valor no input do `setMarketConfig` (o ativo sempre na lista).
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7).
 *
 * Contrato: PSP inalterado desde a abertura → mantém o `fiscal_provider`
 * gravado (ex.: Paddle MoR da IT); trocado → `defaultFiscalProviderFor(provider)`.
 * `allowed_payment_providers` começa sempre pelo ativo, sem repetição.
 *
 * Usado por: `MarketConfigEditDialog.submit` e `CountryEditDialog`, admin web.
 *
 * Falhas conhecidas: auditoria #756 (achado 1, alto) — ver `marketPspInitial`.
 */
export function marketPspPayload(v: MarketPspValue): MarketPspPayload {
  const { provider, mode } = splitPspChoice(v.choice);
  return {
    payment_provider: provider,
    payment_mode: mode,
    allowed_payment_providers: [provider, ...v.extraAllowed.filter((p) => p !== provider)],
    // PSP inalterado → o fiscal gravado (ex.: Paddle MoR da IT); trocado → o do PSP.
    fiscal_provider: isInitialChoice(v) && v.initial ? v.initial.fiscal : defaultFiscalProviderFor(provider),
  };
}

